import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const MountPleasant = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.mount-pleasant-vancouver:About Mount Pleasant Paragraph">
          Mount Pleasant, located in{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">BC</LinkText>, is a
          vibrant and eclectic neighborhood known for its artistic flair and
          community spirit. Bounded by Cambie Street to the west, Clark Drive to
          the east, Great Northern Way and 2nd Avenue to the north, and 16th
          Avenue and Kingsway to the south, this area has transformed from its
          working-class roots into a hub for young professionals, artists, and
          families. The neighborhood boasts a mix of heritage buildings and
          modern developments, reflecting its rich history and contemporary
          growth. Main Street, the area’s core, is lined with independent
          boutiques, vintage shops, cafes, and eateries, offering a unique blend
          of old and new Vancouver.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,450 - $2,600',
          bed2: '$3,300 - $4,000',
          bed3: '$5,000 - $6,900'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Apartment Hunting Mount Pleasant Paragraph 1">
            Finding an apartment for rent in Mount Pleasant, Vancouver, BC
            requires careful planning and consideration. Start by determining
            your criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Mount Pleasant lifestyle by exploring
            your new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Apartment Hunting Mount Pleasant Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Apartments for rent in Mount Pleasant Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Mount Pleasant, Vancouver
              </LinkText>
              , cater to a diverse demographic, including young professionals,
              artists, and students. The neighborhood offers a variety of rental
              options, from modern high-rise condos to charming low-rise
              buildings. Residents benefit from proximity to public transit,
              vibrant shopping districts, and a lively arts scene, making it an
              attractive place to live.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Houses for rent in Mount Pleasant Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Mount Pleasant, Vancouver
              </LinkText>
              , are ideal for families and individuals seeking more space while
              staying connected to urban amenities. The area features a mix of
              heritage homes and contemporary houses, often with private yards
              and tree-lined streets. Renting a house here provides a suburban
              feel within the city’s dynamic environment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Townhouses for rent in Mount Pleasant Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Mount Pleasant, Vancouver
              </LinkText>{' '}
              , offer a balance between apartment living and standalone homes,
              appealing to families and professionals alike. These multi-level
              units often come with modern designs and private outdoor spaces.
              The neighborhood’s walkability and access to cultural hotspots
              enhance the appeal of townhouse living.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Basements for rent in Mount Pleasant Paragraph">
              Basements for rent in Mount Pleasant, Vancouver, provide
              affordable housing options suitable for students and young
              professionals. These units typically offer private entrances and
              essential amenities. Renting a basement suite in this neighborhood
              allows residents to enjoy the vibrant community without a hefty
              price tag.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Condos for rent Mount Pleasant Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Mount Pleasant, Vancouver
              </LinkText>
              , attract those seeking modern amenities and a low-maintenance
              lifestyle. Many condominiums in the area feature contemporary
              designs, fitness centers, and communal spaces. The neighborhood’s
              thriving arts scene and eclectic dining options make condo living
              particularly appealing.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Rooms for rent in Mount Pleasant Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Mount Pleasant, Vancouver
              </LinkText>{' '}
              , are ideal for individuals seeking cost-effective living
              arrangements. These accommodations often involve shared common
              areas, fostering a sense of community among tenants. The
              neighborhood’s central location ensures easy access to public
              transportation, educational institutions, and employment hubs.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Short term rentals in Mount Pleasant Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Mount Pleasant, Vancouver
              </LinkText>
              , cater to tourists, business travelers, and individuals in
              transition. These accommodations range from fully furnished
              apartments to private rooms, offering flexibility and convenience.
              Staying in Mount Pleasant on a short-term basis allows visitors to
              experience the neighborhood’s vibrant culture and proximity to
              major attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Exploring Mount Pleasant Paragraph">
          Mount Pleasant is a dynamic neighborhood that seamlessly blends
          historic charm with modern vibrancy. Known for its artistic community,
          the area is adorned with colorful murals, especially along{' '}
          <LinkText href="https://govancity.com/vancouver-shopping-districts/main-street/">
            Main Street
          </LinkText>
          , reflecting its creative spirit. The neighborhood hosts the annual{' '}
          <LinkText href="https://vanmuralfest.ca/">
            Vancouver Mural Festival
          </LinkText>
          , attracting artists and visitors from around the globe. Key
          attractions include{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/new-public-art-in-guelph-park.aspx">
            Dude Chilling Park
          </LinkText>
          , a testament to the area’s quirky character, and the plethora of
          craft breweries that pay homage to the history of Brewery Creek. With
          its mix of heritage homes and contemporary architecture, Mount
          Pleasant offers a unique urban experience that appeals to a wide
          demographic.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Things To Do Mount Pleasant Paragraph">
          Mount Pleasant offers a plethora of activities catering to diverse
          interests. Art enthusiasts can embark on a self-guided tour of the
          neighborhood’s vibrant murals, a legacy of the Vancouver Mural
          Festival. Beer aficionados will appreciate the numerous craft
          breweries, such as Main Street Brewing and Brassneck Brewery, offering
          a taste of the local brew culture. Shoppers can explore an array of
          vintage boutiques and independent stores along Main Street, providing
          unique fashion and artisan goods. For those seeking relaxation, the
          neighborhood’s parks, including Dude Chilling Park, offer green spaces
          perfect for leisure. Foodies will delight in the eclectic eateries,
          ranging from vegan cafes to international cuisine, reflecting
          Vancouver’s multicultural tapestry.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Restaurants Mount Pleasant Paragraph">
        <Paragraph>
          Mount Pleasant Vancouver offers a range of dining options for every
          taste. Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://www.burdockandco.com/">
            Burdock & Co.
          </LinkText>{' '}
          - Burdock & Co. is a celebrated restaurant known for its farm-to-table
          approach, offering dishes crafted from locally sourced, organic
          ingredients. The menu features innovative plates like the buttermilk
          fried chicken and seasonal vegetable creations. The rustic-chic
          ambiance, complemented by warm lighting and wooden accents, provides
          an intimate dining experience. It’s an ideal spot for food enthusiasts
          seeking sustainable and thoughtfully prepared meals.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://publishedonmain.com/">
            Published on Main
          </LinkText>{' '}
          - Published on Main offers an elevated dining experience with a focus
          on contemporary Canadian cuisine. The menu showcases dishes like
          dry-aged duck and Pacific octopus, emphasizing local and seasonal
          ingredients. The sleek, modern interior, combined with attentive
          service, makes it suitable for special occasions and culinary
          indulgence.
        </Paragraph>
        <Paragraph>
          3) <LinkText href="https://anhandchi.com/">Anh and Chi</LinkText> -
          Anh and Chiis a stylish and modern Vietnamese restaurant in Mount
          Pleasant, Vancouver, known for its flavorful dishes and vibrant
          atmosphere. Family-owned and Michelin-recommended, it offers classics
          like pho, banh mi, and lemongrass chicken, alongside creative
          cocktails. The chic decor and warm hospitality make it a popular spot
          for both casual dining and special occasions.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Transportation Mount Pleasant Paragraph">
          Getting around Mount Pleasant, Vancouver, BC, is convenient with
          various transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Mount Pleasant are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Mount Pleasant offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.mount-pleasant-vancouver:Where To Stay Mount Pleasant Paragraph">
          Mount Pleasant offers a great mix of accommodation options that suit a
          variety of travelers. For a boutique experience,{' '}
          <LinkText href="https://www.marriott.com/en-us/hotels/yvrps-the-douglas-autograph-collection/overview/?msockid=16018a3ffea869d81fa19f8cff11682b">
            The Douglas, Autograph Collection
          </LinkText>
          , located nearby in False Creek, offers luxurious rooms with modern
          amenities and stylish decor—perfect for business travelers or couples.
          Another upscale option is{' '}
          <LinkText href="https://opushotel.com/">OPUS Vancouver</LinkText>,
          just minutes away in Yaletown, known for its trendy vibe and
          personalized service. Short-term rentals are also abundant, with{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText> offering
          everything from cozy private rooms in character homes to full
          apartments with views of the city skyline. These are ideal for
          families, digital nomads, or anyone looking to live like a local.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default MountPleasant
